"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.mapScenarios = exports.asynchronousMapDataToObject = void 0;
const index_1 = require("./index");
const Nodes_1 = require("./Nodes");
const Cache_1 = __importDefault(require("./Cache"));
const lodash_1 = __importDefault(require("lodash"));
const asynchronousMapDataToObject = async (mapDefinition, accessors, configFunctions, timestamp, parentNodeId) => {
    //build an empty cache mapping node ids to the objects they return
    const localMapDefinition = lodash_1.default.cloneDeep(mapDefinition);
    const cache = new Cache_1.default();
    const nodes = localMapDefinition.nodes ? localMapDefinition.nodes : {};
    //build the nodeMap used to resolve the values of nodes
    const nodeMap = {};
    const addToCache = (name, value) => {
        cache.addValue(name, value);
    };
    const cachedResolver = (nodeMap, nodeId) => {
        const cacheValue = cache.getValue(nodeId);
        if (cacheValue) {
            return Promise.resolve(cacheValue);
        }
        else {
            const nodeDefinition = localMapDefinition.nodes[nodeId];
            //This resolver implementation is mostly here to work around typesccript class instantiation
            const resolver = async (nodeClass) => {
                const nodeInstance = new nodeClass(Object.assign(Object.assign({}, nodeDefinition), { accessorMap: accessors, mapScenarios: exports.mapScenarios }));
                const nodeIds = parentNodeId ? `${parentNodeId}-${nodeId}` : nodeId;
                const valuePromise = nodeInstance.asynchronousResolver(nodeMap, configFunctions, {
                    parentNodeId: nodeIds,
                    timestamp: timestamp,
                    seed: timestamp ? `${nodeIds}-${timestamp.toISOString()}` : null,
                });
                let value = await valuePromise;
                //recursively add new nodes if the node is set to iterate its values.
                if (nodeInstance.iterate && Array.isArray(value.output)) {
                    value = (0, index_1.iterateValues)(value.output, nodeId, addToCache, nodeMap, localMapDefinition, cachedResolver);
                }
                if (value !== null) {
                    return value;
                }
            };
            const NodeClass = Nodes_1.AllNodeMap[nodeDefinition.type];
            const r = resolver(NodeClass);
            cache.addValue(nodeId, r);
            return r;
        }
    };
    Object.keys(nodes).forEach(nId => {
        nodeMap[nId] = cachedResolver;
    });
    const outputDef = localMapDefinition.outputDefinition;
    if (!outputDef) {
        //This should never be reached, but a bad config could cause this
        return {};
    }
    if (outputDef && outputDef.outputNode) {
        const outNode = nodeMap[outputDef.outputNode];
        return outNode ? outNode(nodeMap, outputDef.outputNode) : {};
    }
    else {
        const outputQuery = outputDef.outputQuery;
        const allNodeIds = Object.keys(nodes);
        const nodeIds = allNodeIds.filter(nodeId => {
            var _a, _b;
            return (outputDef.outputNodes && outputDef.outputNodes.includes(nodeId)) ||
                (outputQuery && ((_a = outputQuery.nodeIds) === null || _a === void 0 ? void 0 : _a.includes(nodeId))) ||
                (outputQuery && ((_b = outputQuery.types) === null || _b === void 0 ? void 0 : _b.includes(localMapDefinition.nodes[nodeId].type)));
        });
        let promises = new Array();
        const keys = new Array();
        //Delete after 5.4.12
        //   const resolveNode = async (id: string): Promise<Record<string, unknown>> => {
        //     const ret = { [id]: await nodeMap[id](nodeMap, id) };
        //       Object.keys(nodeMap).forEach(key => {
        //         //This will find any output nodes that were duplicated
        //         if (key.match(id + '-iteration')) {
        //           keys.push(key);
        //         }
        //       });
        //     return ret;
        //   };
        const allKeys = nodeIds;
        let previousCount = Object.keys(nodeMap).length;
        let more = false;
        const resolveNode = async (id) => {
            const ret = { [id]: await nodeMap[id](nodeMap, id) };
            if (Object.keys(nodeMap).length !== previousCount || more) {
                more = false;
                Object.keys(nodeMap).forEach(key => {
                    //This will find any output nodes that were duplicated
                    if (key.match(id + '-iteration')) {
                        keys.push(key);
                        allKeys.push(key);
                    }
                    else if (!more &&
                        !allKeys.filter(k => k === key).length &&
                        allKeys.filter(k => key.match(k + '-iteration')).length) {
                        more = true;
                    }
                });
                previousCount = Object.keys(nodeMap).length;
            }
            return ret;
        };
        promises = nodeIds.map(async (nodeId) => {
            // get the value of the current output node
            return resolveNode(nodeId);
        }, {});
        const resolvePromises = async () => {
            if (keys.length) {
                const keysToProcess = keys.splice(0, Math.min(keys.length, 100));
                promises = promises.concat(
                //Add all of the duplicated keys to the list of promises
                keysToProcess.map(async (nodeId) => {
                    // get the value of the current output node
                    return await resolveNode(nodeId);
                }, {}));
            }
            //Using this to resolve promises because we have the ability to add on to them in the process of resolving
            return Promise.all(promises).then(resolved => {
                if (keys.length) {
                    // The list of promises or values changed. Return a new Promise.
                    // The original promise won't resolve until the new one does.
                    return resolvePromises();
                }
                // The list of promises or values stayed the same.
                // Return results immediately.
                return resolved;
            });
        };
        return resolvePromises().then(values => {
            return values.reduce((acc, value) => {
                return Object.assign(Object.assign({}, acc), value);
            }, {});
        });
    }
};
exports.asynchronousMapDataToObject = asynchronousMapDataToObject;
//Internal nodes, are nodes that should never appear in the tool palette, like FormError, FormWarning, etc.
const internalNodes = [
    'FORM-ERROR',
    'FORM-WARNING',
    'DISPLAY-OPTIONS',
    'FORM-CONDITIONAL',
    'OBJECT-BUILDER',
    'BUTTON-STATE',
    'OUTPUT-EVENT',
    'ARRAY-ITERATOR',
    'DROPDOWN-OPTIONS',
];
//These are nodes that represent available inputs and arent' applicable in all scenarios
const inputNodes = [
    'EVENT',
    'METADATA',
    'STATE',
    'FORM',
    'FORMVALUE',
    'DATA',
    'COMMAND_DATA',
];
//These are all nodes that should be available for the user to create in most maps
const userCreateableNodes = Object.keys(Nodes_1.AllNodeMap).filter((nodeType) => !internalNodes.includes(nodeType) && !inputNodes.includes(nodeType));
const asyncEvaluator = (map, accessors, configFunctions, timestamp, parentNodeId) => {
    return (0, exports.asynchronousMapDataToObject)(map, accessors, configFunctions, timestamp, parentNodeId).then(result => {
        return result;
    });
};
//TODO: Can't really make this better defined at the moment. Need more tricks
exports.mapScenarios = {
    EVENT_AGGREGATE_MAP: {
        availableNodes: [...userCreateableNodes, 'EVENT', 'STATE'],
        defaultNodeMap: {
            nodes: {
                EVENT: { type: 'EVENT', name: 'Event', inputs: {} },
                STATE: { type: 'STATE', name: 'Current State', inputs: {} },
                OUTPUT: { type: 'OBJECT-BUILDER', name: 'Output State', inputs: {}, config: { objectSchema: 'STATE' } },
            },
            outputDefinition: {
                outputNode: 'OUTPUT',
            },
        },
        requiredNodes: [{ nodeId: 'OUTPUT', nodeType: 'OBJECT-BUILDER' }],
        evaluate: asyncEvaluator,
    },
    DERIVED_PROPERTY_MAPPING: {
        availableNodes: [...userCreateableNodes, 'STATE'],
        defaultNodeMap: {
            nodes: {
                STATE: { type: 'STATE', name: 'Current State', inputs: {} },
                OUTPUT: { type: 'OBJECT-BUILDER', name: 'Output State', inputs: {}, config: { objectSchema: 'STATE' } },
            },
            outputDefinition: {
                outputNode: 'OUTPUT',
            },
        },
        requiredNodes: [{ nodeId: 'OUTPUT', nodeType: 'OBJECT-BUILDER' }],
        evaluate: asyncEvaluator,
    },
    INITIAL_DATA_MAPPING: {
        availableNodes: [...userCreateableNodes, 'STATE', 'METADATA'],
        defaultNodeMap: {
            nodes: {
                STATE: { type: 'STATE', name: 'Current State', inputs: {} },
                OUTPUT: { type: 'OBJECT-BUILDER', name: 'Form State', inputs: {}, config: { objectSchema: 'FORM' } },
            },
            outputDefinition: {
                outputNode: 'OUTPUT',
            },
        },
        requiredNodes: [{ nodeId: 'OUTPUT', nodeType: 'OBJECT-BUILDER' }],
        evaluate: asyncEvaluator,
    },
    COMPUTED_MAPPING: {
        availableNodes: [...userCreateableNodes, 'FORM', 'STATE', 'METADATA', 'FORMVALUE'],
        defaultNodeMap: {
            nodes: {
                FORM: { type: 'FORM', name: 'Form', inputs: {} },
                FORMVALUE: { type: 'FORMVALUE', name: 'Value', inputs: {} },
                OUTPUT: { type: 'OBJECT-BUILDER', name: 'Result', inputs: {}, config: { objectSchema: 'COMPUTED' } },
            },
            outputDefinition: {
                outputNode: 'OUTPUT',
            },
        },
        requiredNodes: [{ nodeId: 'OUTPUT', nodeType: 'OBJECT-BUILDER' }],
        evaluate: asyncEvaluator,
    },
    COMMAND_EVENT_MAPPING: {
        availableNodes: [...userCreateableNodes, 'DATA', 'METADATA', 'COMMAND_DATA'],
        defaultNodeMap: {
            nodes: {
                COMMAND: { type: 'DATA', name: 'Command' },
                OUTPUT: { type: 'OBJECT-BUILDER', name: 'Event', inputs: {}, config: { objectSchema: 'EVENT' } },
            },
            outputDefinition: {
                outputNode: 'OUTPUT',
            },
        },
        requiredNodes: [{ nodeId: 'OUTPUT', nodeType: 'OBJECT-BUILDER' }],
        evaluate: asyncEvaluator,
    },
    FUNCTION_MAPPING: {
        availableNodes: [...lodash_1.default.without(userCreateableNodes, 'FUNCTION'), 'DATA'],
        defaultNodeMap: {
            nodes: {
                COMMAND: { type: 'DATA', name: 'Input' },
                OUTPUT: { type: 'OBJECT-BUILDER', name: 'Output', inputs: {}, config: { objectSchema: 'STATE' } },
            },
            outputDefinition: {
                outputNode: 'OUTPUT',
            },
        },
        requiredNodes: [{ nodeId: 'OUTPUT', nodeType: 'OBJECT-BUILDER' }],
        evaluate: asyncEvaluator,
    },
    COMMAND_MULTI_EVENT_MAPPING: {
        availableNodes: [...userCreateableNodes, 'DATA', 'METADATA', 'OUTPUT-EVENT', 'ARRAY-ITERATOR', 'COMMAND_DATA'],
        defaultNodeMap: {
            nodes: {
                COMMAND: { type: 'DATA', name: 'Command' },
                OUTPUT: { type: 'OUTPUT-EVENT', name: 'Output Event', inputs: {}, config: { objectSchema: 'EVENT' } },
            },
            outputDefinition: {
                outputQuery: {
                    types: ['OUTPUT-EVENT'],
                },
            },
        },
        requiredNodes: [{ nodeId: 'OUTPUT', nodeType: 'OBJECT-BUILDER' }],
        evaluate: (map, accessors, configFunctions, timestamp, parentNodeId) => {
            return (0, exports.asynchronousMapDataToObject)(map, accessors, configFunctions, timestamp, parentNodeId).then(result => 
            //Filters out any null values returned
            Object.values(result).filter(val => !!val));
        },
    },
    COMMAND_ZEBRA_MAPPING: {
        availableNodes: [...userCreateableNodes, 'DATA', 'METADATA', 'ARRAY-ITERATOR', 'COMMAND_DATA'],
        defaultNodeMap: {
            nodes: {
                COMMAND: { type: 'DATA', name: 'Command' },
                ZEBRA_OPTIONS: { type: 'ZEBRA-OPTIONS', inputs: {} },
            },
            outputDefinition: {
                outputNode: 'ZEBRA_OPTIONS',
            },
        },
        requiredNodes: [{ nodeId: 'ZEBRA_OPTIONS', nodeType: 'ZEBRA-OPTIONS' }],
        evaluate: asyncEvaluator,
    },
    FORM_LEVEL_ERROR: {
        availableNodes: [...userCreateableNodes, 'FORM', 'STATE', 'METADATA'],
        defaultNodeMap: {
            nodes: {
                FORM: { type: 'FORM', name: 'Form', inputs: {} },
                FORM_ERROR: { type: 'FORM-ERROR', inputs: {} },
            },
            outputDefinition: {
                outputNode: 'FORM_ERROR',
            },
        },
        requiredNodes: [{ nodeId: 'FORM_ERROR', nodeType: 'FORM-ERROR' }],
        evaluate: asyncEvaluator,
    },
    FORM_FIELD_CONDITIONAL: {
        availableNodes: [...userCreateableNodes, 'FORM', 'STATE', 'METADATA', 'FORMVALUE'],
        defaultNodeMap: {
            nodes: {
                FORM: { type: 'FORM', name: 'Form', inputs: {} },
                FORMVALUE: { type: 'FORMVALUE', name: 'Value', inputs: {} },
                FORM_CONDITIONAL: { type: 'FORM-CONDITIONAL', inputs: {} },
            },
            outputDefinition: {
                outputNode: 'FORM_CONDITIONAL',
            },
        },
        requiredNodes: [{ nodeId: 'FORM_CONDITIONAL', nodeType: 'FORM-CONDITIONAL' }],
        evaluate: asyncEvaluator,
    },
    FORM_LEVEL_WARNING: {
        availableNodes: [...userCreateableNodes, 'FORM', 'STATE', 'METADATA'],
        defaultNodeMap: {
            nodes: {
                FORM: { type: 'FORM', name: 'Form', inputs: {} },
                FORM_WARNING: { type: 'FORM-WARNING', inputs: {} },
            },
            outputDefinition: {
                outputNode: 'FORM_WARNING',
            },
        },
        requiredNodes: [{ nodeId: 'FORM_WARNING', nodeType: 'FORM-WARNING' }],
        evaluate: asyncEvaluator,
    },
    FIELD_LEVEL_ERROR: {
        availableNodes: [...userCreateableNodes, 'FORM', 'STATE', 'METADATA', 'FORMVALUE'],
        defaultNodeMap: {
            nodes: {
                FORM: { type: 'FORM', name: 'Form', inputs: {} },
                FORMVALUE: { type: 'FORMVALUE', name: 'Value', inputs: {} },
                FIELD_ERROR: { type: 'FORM-ERROR', name: 'Field Error' },
            },
            outputDefinition: {
                outputNode: 'FIELD_ERROR',
            },
        },
        requiredNodes: [{ nodeId: 'FIELD_ERROR', nodeType: 'FORM-ERROR' }],
        evaluate: asyncEvaluator,
    },
    FIELD_LEVEL_WARNING: {
        availableNodes: [...userCreateableNodes, 'FORM', 'STATE', 'METADATA', 'FORMVALUE'],
        defaultNodeMap: {
            nodes: {
                FORM: { type: 'FORM', name: 'Form', inputs: {} },
                FORMVALUE: { type: 'FORMVALUE', name: 'Value', inputs: {} },
                FIELD_WARNING: { type: 'FORM-WARNING', name: 'Field Warning' },
            },
            outputDefinition: {
                outputNode: 'FIELD_WARNING',
            },
        },
        requiredNodes: [{ nodeId: 'FIELD_WARNING', nodeType: 'FORM-WARNING' }],
        evaluate: asyncEvaluator,
    },
    DISPLAY_OPTIONS: {
        availableNodes: [...userCreateableNodes, 'FORM', 'STATE', 'METADATA', 'FORMVALUE'],
        defaultNodeMap: {
            nodes: {
                FORM: { type: 'FORM', name: 'Form', inputs: {} },
                FORMVALUE: { type: 'FORMVALUE', name: 'Value', inputs: {} },
                DISPLAY_OPTIONS: { type: 'DISPLAY-OPTIONS', name: 'Display Options' },
            },
            outputDefinition: {
                outputNode: 'DISPLAY_OPTIONS',
            },
        },
        requiredNodes: [{ nodeId: 'DISPLAY_OPTIONS', nodeType: 'DISPLAY-OPTIONS' }],
        evaluate: asyncEvaluator,
    },
    DROPDOWN_OPTIONS: {
        availableNodes: [...userCreateableNodes, 'FORM', 'STATE', 'METADATA', 'FORMVALUE'],
        defaultNodeMap: {
            nodes: {
                FORM: { type: 'FORM', name: 'Form', inputs: {} },
                FORMVALUE: { type: 'FORMVALUE', name: 'Value', inputs: {} },
                DROPDOWN_OPTIONS: { type: 'DROPDOWN-OPTIONS', name: 'Options' },
            },
            outputDefinition: {
                outputNode: 'DROPDOWN_OPTIONS',
            },
        },
        requiredNodes: [{ nodeId: 'DROPDOWN_OPTIONS', nodeType: 'DROPDOWN-OPTIONS' }],
        evaluate: asyncEvaluator,
    },
    BUTTON_STATE: {
        availableNodes: [...userCreateableNodes, 'STATE', 'METADATA'],
        defaultNodeMap: {
            nodes: {
                STATE: { type: 'STATE' },
                BUTTON_STATE: { type: 'BUTTON-STATE', name: 'Button State' },
            },
            outputDefinition: {
                outputNode: 'BUTTON_STATE',
            },
        },
        requiredNodes: [{ nodeId: 'BUTTON_STATE', nodeType: 'BUTTON-STATE' }],
        evaluate: asyncEvaluator,
    },
    FAB_BUTTON_STATE: {
        availableNodes: [...userCreateableNodes, 'METADATA'],
        defaultNodeMap: {
            nodes: {
                METADATA: { type: 'METADATA' },
                BUTTON_STATE: { type: 'BUTTON-STATE', name: 'Button State' },
            },
            outputDefinition: {
                outputNode: 'BUTTON_STATE',
            },
        },
        requiredNodes: [{ nodeId: 'BUTTON_STATE', nodeType: 'BUTTON-STATE' }],
        evaluate: asyncEvaluator,
    },
    CARD_ELEMENT_MAP: {
        availableNodes: [...userCreateableNodes, 'STATE', 'METADATA'],
        defaultNodeMap: {
            nodes: {
                STATE: { type: 'STATE', name: 'Current State', inputs: {} },
                OUTPUT: { type: 'OBJECT-BUILDER', name: 'Output', inputs: {}, config: { objectSchema: 'FORM' } },
            },
            outputDefinition: {
                outputNode: 'OUTPUT',
            },
        },
        requiredNodes: [{ nodeId: 'OUTPUT', nodeType: 'OBJECT-BUILDER' }],
        evaluate: asyncEvaluator,
    },
    NAVIGATION_OPTIONS: {
        availableNodes: [...userCreateableNodes, 'STATE', 'METADATA'],
        defaultNodeMap: {
            nodes: {
                STATE: { type: 'STATE' },
                NAVIGATION_OPTIONS: { type: 'NAVIGATION-OPTIONS', name: 'Navigation Options' },
            },
            outputDefinition: {
                outputNode: 'NAVIGATION_OPTIONS',
            },
        },
        requiredNodes: [{ nodeId: 'NAVIGATION_OPTIONS', nodeType: 'NAVIGATION-OPTIONS' }],
        evaluate: asyncEvaluator,
    },
};
/*const MapAsyncScenarioExecutor = async (
  mapDefinition: NodeMapDefinition,
  accessors?: AccessorMap
): Promise<Record<string, unknown>> => {};
*/
//# sourceMappingURL=MapScenarios.js.map